// Okno wyboru sprzętu (U.27): filtry, liczniki kategorii i listy opcji.
//
// Jedno okno (`components/InventoryModal.js`) obsługuje nową rezerwację, edycję rezerwacji,
// kalendarz sprzętu i ofertę, więc reguły filtrowania siedzą tutaj, bez Reacta, żeby dało się
// je sprawdzić testem. Okno jest uniwersalne: marka, rozmiar i klasa bywają puste (od E.5),
// a wtedy nie mają ani filtra, ani kolumny, ani pustej pozycji na liście opcji.

import { foldText } from './foldText';

export const EMPTY_FILTERS = Object.freeze({
  query: '',
  category: '',
  size: '',
  brand: '',
  class: '',
  onlySelected: false,
});

/** Filtry zawężające po wartości pola; kolejność = kolejność w pasku okna. */
export const FACET_KEYS = ['size', 'brand', 'class'];

/** Identyfikatory przychodzą raz jako liczby, raz jako tekst („12”); porównujemy po liczbie. */
export function normalizeId(v) {
  if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
  return v;
}

export function toIdSet(ids) {
  return new Set((Array.isArray(ids) ? ids : []).map(normalizeId));
}

const text = (v) => (v === null || v === undefined ? '' : String(v).trim());

export function hasValue(v) {
  return text(v) !== '';
}

/** Cena za dzień z cennika sztuki; `null`, gdy lista jej nie niesie (0 to cena, nie brak). */
export function dailyPrice(eq) {
  const raw = eq ? (eq.price_per_day ?? eq.price) : null;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

const searchText = (eq) => [eq.name, eq.inventory_number, eq.brand].map(foldText).join(' ');

const searchWords = (query) => {
  const q = foldText(query).trim();
  return q ? q.split(/\s+/) : [];
};

/** Szukanie po nazwie, numerze inwentarzowym i marce; każde słowo frazy musi gdzieś pasować. */
export function matchesQuery(eq, query) {
  const words = searchWords(query);
  if (words.length === 0) return true;
  const hay = searchText(eq);
  return words.every((word) => hay.includes(word));
}

const INDEXED = Symbol('inventoryIndex');

/**
 * Indeks listy: pola każdej sztuki sprowadzone do postaci porównywalnej RAZ na listę. Bez niego
 * każdy wpisany znak i każde zaznaczenie wiersza składało od nowa tekst trzech pól każdej sztuki
 * (3000 sztuk: ok. 11 ms na zdarzenie wobec 0,4 ms z indeksem). Wszystkie funkcje poniżej
 * przyjmują zwykłą listę albo gotowy indeks; okno buduje go raz na listę.
 */
export function indexEquipment(list) {
  if (list && list[INDEXED]) return list;
  const rows = (Array.isArray(list) ? list : []).map((eq) => ({
    eq,
    id: normalizeId(eq.id),
    category: text(eq.category),
    size: text(eq.size),
    brand: text(eq.brand),
    class: text(eq.class),
    hay: searchText(eq),
  }));
  Object.defineProperty(rows, INDEXED, { value: true });
  return rows;
}

const emptyValueSets = () => ({ size: new Set(), brand: new Set(), class: new Set() });
const NO_VALUES = emptyValueSets();
const facetCache = new WeakMap();

/** Wartości pól w całej liście i osobno w każdej kategorii; liczone raz na indeks. */
function facetValues(index) {
  let cached = facetCache.get(index);
  if (cached) return cached;
  const all = emptyValueSets();
  const byCategory = new Map();
  for (const row of index) {
    let own = null;
    if (row.category) {
      own = byCategory.get(row.category);
      if (!own) {
        own = emptyValueSets();
        byCategory.set(row.category, own);
      }
    }
    for (const key of FACET_KEYS) {
      if (!row[key]) continue;
      all[key].add(row[key]);
      if (own) own[key].add(row[key]);
    }
  }
  cached = { all, byCategory };
  facetCache.set(index, cached);
  return cached;
}

const valuesIn = (index, category) => {
  const values = facetValues(index);
  return category ? (values.byCategory.get(category) || NO_VALUES) : values.all;
};

const matchesFacets = (row, f) => FACET_KEYS.every((key) => !f[key] || row[key] === f[key]);

/** Szukanie i „tylko wybrane”: część warunku wspólna dla listy i dla wszystkich pigułek. */
function basePredicate(f, selectedSet) {
  const words = searchWords(f.query);
  return (row) => (!f.onlySelected || selectedSet.has(row.id))
    && words.every((word) => row.hay.includes(word));
}

/** Lista po wszystkich filtrach, w kolejności wejścia. */
export function filterEquipment(source, filters = EMPTY_FILTERS, selectedSet = new Set()) {
  const index = indexEquipment(source);
  const f = { ...EMPTY_FILTERS, ...filters };
  const base = basePredicate(f, selectedSet);
  return index
    .filter((row) => (!f.category || row.category === f.category) && matchesFacets(row, f) && base(row))
    .map((row) => row.eq);
}

/** Po zmianie kategorii: filtr pola, którego wartości w niej nie ma, zostaje wyczyszczony. */
export function pruneFilters(filters, source) {
  const index = indexEquipment(source);
  const next = { ...EMPTY_FILTERS, ...filters };
  const values = valuesIn(index, next.category);
  for (const key of FACET_KEYS) {
    if (next[key] && !values[key].has(next[key])) next[key] = '';
  }
  return next;
}

/**
 * Po zmianie LISTY (np. świeża dostępność przy otwartym oknie): kategoria i wartości pól, których
 * już w niej nie ma, znikają z filtrów. Bez tego filtr zostawał w stanie, pole wyboru pokazywało
 * „Wszystkie”, a lista była pusta. Zwraca TEN SAM obiekt, gdy nic się nie zmieniło.
 */
export function sanitizeFilters(filters, source) {
  const index = indexEquipment(source);
  const f = { ...EMPTY_FILTERS, ...filters };
  const category = f.category && facetValues(index).byCategory.has(f.category) ? f.category : '';
  const next = pruneFilters({ ...f, category }, index);
  const changed = next.category !== f.category || FACET_KEYS.some((key) => next[key] !== f[key]);
  return changed ? next : filters;
}

/**
 * Pigułki kategorii. Zestaw i kolejność wynikają z CAŁEJ listy (od najliczniejszej, przy remisie
 * alfabetycznie), więc pigułki nie przeskakują w trakcie pisania.
 *
 * Liczba przy pigułce mówi, ILE WIERSZY POKAŻE LISTA PO JEJ KLIKNIĘCIU; przy zaznaczonej jest to
 * więc liczba wierszy widocznych teraz. Kliknięcie pigułki czyści filtr pola, którego wartości w tej
 * kategorii nie ma (`pruneFilters`), i liczba to uwzględnia: przy wybranym rozmiarze nart „Buty”
 * pokazują wszystkie buty pasujące do szukania, a nie zero. Pigułka z zerem naprawdę prowadzi do
 * pustej listy. Skutek uboczny: przy aktywnym filtrze pola suma pigułek może różnić się od
 * „Wszystkie”, a sztuka bez kategorii liczy się wyłącznie we „Wszystkie”.
 */
export function categoryChips(source, filters = EMPTY_FILTERS, selectedSet = new Set()) {
  const index = indexEquipment(source);
  const f = { ...EMPTY_FILTERS, ...filters };
  const base = basePredicate(f, selectedSet);
  const afterClick = new Map();
  const facetsAfterClick = (category) => {
    let pruned = afterClick.get(category);
    if (!pruned) {
      pruned = pruneFilters({ ...f, category }, index);
      afterClick.set(category, pruned);
    }
    return pruned;
  };
  const allCategories = facetsAfterClick('');
  const totals = new Map();
  const counts = new Map();
  let total = 0;
  for (const row of index) {
    if (row.category) totals.set(row.category, (totals.get(row.category) || 0) + 1);
    if (!base(row)) continue;
    if (matchesFacets(row, allCategories)) total += 1;
    if (row.category && matchesFacets(row, facetsAfterClick(row.category))) {
      counts.set(row.category, (counts.get(row.category) || 0) + 1);
    }
  }
  const chips = [...totals.keys()]
    .sort((a, b) => totals.get(b) - totals.get(a) || a.localeCompare(b, 'pl'))
    .map((value) => ({ value, count: counts.get(value) || 0 }));
  return { total, chips };
}

const asNumber = (v) => {
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Rozmiary liczbowe rosnąco (150, 160, 27,5), opisowe (S, M, L) po nich, alfabetycznie. */
function compareValues(a, b) {
  const na = asNumber(a);
  const nb = asNumber(b);
  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;
  return a.localeCompare(b, 'pl', { numeric: true, sensitivity: 'base' });
}

/** Różne, niepuste wartości pola: nigdy pustej pozycji dla sprzętu bez marki albo rozmiaru. */
export function facetOptions(source, key) {
  return [...facetValues(indexEquipment(source)).all[key]].sort(compareValues);
}

/**
 * Filtry pól dla paska okna. `visible` wynika z całej listy (filtr ma sens, gdy jest z czego
 * wybierać, czyli przy co najmniej dwóch wartościach) i nie zmienia się przy przełączaniu
 * kategorii, żeby pasek nie skakał; `options` to wartości w wybranej kategorii.
 */
export function facets(source, filters = EMPTY_FILTERS) {
  const index = indexEquipment(source);
  const { category } = { ...EMPTY_FILTERS, ...filters };
  const all = facetValues(index).all;
  const scoped = valuesIn(index, category);
  const out = {};
  for (const key of FACET_KEYS) {
    out[key] = {
      visible: all[key].size >= 2,
      options: [...scoped[key]].sort(compareValues),
    };
  }
  return out;
}

/** Kolumny listy: tylko te, dla których choć jedna sztuka ma wartość. */
export function visibleColumns(source) {
  const index = indexEquipment(source);
  const values = facetValues(index);
  return {
    category: values.byCategory.size > 0,
    brand: values.all.brand.size > 0,
    size: values.all.size.size > 0,
    class: values.all.class.size > 0,
    number: index.some((row) => hasValue(row.eq.inventory_number)),
    price: index.some((row) => dailyPrice(row.eq) !== null),
  };
}

/** Czy cokolwiek zawęża listę (do przycisku „Wyczyść filtry” i wyboru pustego stanu). */
export function hasActiveFilters(filters) {
  const f = { ...EMPTY_FILTERS, ...filters };
  return Boolean(f.query.trim() || f.category || f.onlySelected || FACET_KEYS.some((key) => f[key]));
}
